/**
 * EE CONFIGURATOR — product configurator engine (mockup page-2 layout).
 * Renders config-driven product cards into mount points and manages a
 * shared "quote basket" with a contact panel that submits via callbacks
 * supplied in window.EEConfiguratorConfig (see configurator.config.js).
 */
(function (global) {
  'use strict';

  const esc = (s) => s == null ? '' : String(s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

  const SHAPE_ICONS = {
    square:    '<svg viewBox="0 0 24 24" fill="currentColor"><rect x="4" y="4" width="16" height="16" rx="1"/></svg>',
    rectangle: '<svg viewBox="0 0 24 24" fill="currentColor"><rect x="2" y="7" width="20" height="10" rx="1"/></svg>',
    circle:    '<svg viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="9"/></svg>',
    triangle:  '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l9 16H3z"/></svg>',
    shield:    '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l8 3v6c0 5-3.4 9.4-8 11-4.6-1.6-8-6-8-11V5z"/></svg>',
    custom:    '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.4 6.9H21l-5.3 4 2 6.9-5.7-4.2-5.7 4.2 2-6.9-5.3-4h6.6z"/></svg>',
  };
  const ICON_INFO = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>';
  const ICON_UPLOAD = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3"/></svg>';

  /* ══════════════════ Product card ══════════════════ */

  class ProductCard {
    constructor(mountEl, def, basket, variantMap) {
      this.root = mountEl;
      this.def = def;
      this.basket = basket;
      // sku -> { id, price (minor units), available }, emitted by the section from
      // real Shopify variants. Null on pages with no buyable products, in which
      // case every card stays in quote mode.
      this.variantMap = variantMap || null;
      this.state = { qty: def.defaultQty || 50, files: [] };
      // seed defaults
      (def.fields || []).forEach((f) => {
        if (f.default !== undefined) this.state[f.id] = f.default;
        if (f.type === 'checks') this.state[f.id] = [];
      });
      this.render();
    }

    val(id) { return this.state[id]; }

    isVisible(f) {
      if (!f.showIf) return true;
      const v = this.val(f.showIf.field);
      if (Array.isArray(f.showIf.anyOf)) return f.showIf.anyOf.includes(v);
      return v === f.showIf.equals;
    }

    isEnabled(f) {
      if (f.minQty && this.state.qty < f.minQty) return false;
      return true;
    }

    render() {
      const d = this.def;
      this.root.innerHTML = `
        <div class="cfg-card" data-product="${esc(d.id)}">
          <div class="cfg-card-head">
            <h3 class="cfg-card-title">${esc(d.title)}</h3>
            <div class="cfg-qty">
              <span class="cfg-qty-label">Quantity (units)</span>
              <div class="cfg-qty-box">
                <button type="button" data-qty="-1" aria-label="Decrease quantity">&minus;</button>
                <input type="number" min="1" value="${this.state.qty}" aria-label="Quantity">
                <button type="button" data-qty="1" aria-label="Increase quantity">+</button>
              </div>
            </div>
          </div>
          <div class="cfg-body">
            <div class="cfg-media">
              <div class="cfg-media-frame">${d.image ? `<img src="${esc(d.image)}" alt="${esc(d.title)}" loading="lazy">` : ''}</div>
              ${d.caption ? `<p class="cfg-media-caption">${esc(d.caption)}</p>` : ''}
              ${d.note ? `<div class="cfg-media-note">${ICON_INFO}<span>${esc(d.note)}</span></div>` : ''}
            </div>
            <div class="cfg-fields" data-fields></div>
          </div>
          <div class="cfg-card-foot">
            <div class="cfg-price" data-price aria-live="polite"></div>
            <span class="cfg-added-msg" data-added>Added to your quote ✓</span>
            <div class="cfg-actions">
              <p class="cfg-quote-reason" data-reason hidden></p>
              <button type="button" class="cfg-btn" data-add>Add to Quote</button>
            </div>
          </div>
        </div>`;

      this.fieldsEl = this.root.querySelector('[data-fields]');
      // delegated: inputs/selects/checkboxes update state without a re-render;
      // 'change' fires after target handlers have stored the new state.
      this.fieldsEl.addEventListener('input', () => this.updatePrice());
      this.fieldsEl.addEventListener('change', () => this.updatePrice());
      this.renderFields();

      const qtyInput = this.root.querySelector('.cfg-qty-box input');
      qtyInput.addEventListener('change', () => {
        this.state.qty = Math.max(1, parseInt(qtyInput.value, 10) || 1);
        qtyInput.value = this.state.qty;
        this.renderFields();
      });
      this.root.querySelectorAll('[data-qty]').forEach((b) => b.addEventListener('click', () => {
        this.state.qty = Math.max(1, this.state.qty + parseInt(b.dataset.qty, 10));
        qtyInput.value = this.state.qty;
        this.renderFields();
      }));
      this.root.querySelector('[data-add]').addEventListener('click', () => {
        if (this._resolved) this.addToCart();
        else this.addToQuote();
      });
    }

    renderFields() {
      const focusInfo = document.activeElement && this.fieldsEl.contains(document.activeElement)
        ? { id: document.activeElement.closest('[data-field]')?.dataset.field, name: document.activeElement.dataset.k }
        : null;
      let n = 0;
      this.fieldsEl.innerHTML = (this.def.fields || []).filter((f) => this.isVisible(f)).map((f) => {
        n += 1;
        return this.renderField(f, n);
      }).join('');
      this.bindFields();
      if (focusInfo && focusInfo.id) {
        const el = this.fieldsEl.querySelector(`[data-field="${focusInfo.id}"] [data-k="${focusInfo.name}"]`);
        if (el) el.focus();
      }
      this.updatePrice();
    }

    /* Can this exact configuration be bought as a real Shopify variant?
       Returns { variant, reason }. A null variant with a reason means the card
       falls back to the quote flow, and the reason is shown to the customer. */
    resolveVariant() {
      const b = this.def.buyable;
      const P = window.EEPricing;
      if (!b || !this.variantMap || !P) return { variant: null, reason: null };
      if (this.state.qty < P.MIN_BUYABLE_QTY) return { variant: null, reason: 'min-qty' };
      const key = b.resolve(this.state);
      if (!key) return { variant: null, reason: null };
      if (key.blocked) return { variant: null, reason: key.blocked };
      const hit = this.variantMap[key.sku];
      if (!hit || !hit.available) return { variant: null, reason: null };
      return { variant: hit, reason: null };
    }

    reasonText(reason) {
      const P = window.EEPricing;
      switch (reason) {
        case 'min-qty':
          return `Online orders start at ${P.MIN_BUYABLE_QTY} units — we'll quote smaller runs individually.`;
        case 'special':
          return 'Specialist threads are priced individually.';
        case 'felt':
          return 'Premium felt backing is priced individually.';
        case 'merrowed':
          return 'Merrowed borders are priced individually.';
        case 'colours':
          return 'Designs over nine colours are priced individually.';
        case 'over-buyable':
          return `Patches over ${P.BUYABLE_MAX_CM2}cm² are priced individually.`;
        case 'over-max':
          return `Patches over ${P.QUOTE_ABOVE_CM2}cm² are priced individually.`;
        default:
          return '';
      }
    }

    /* Swaps the footer button between buying and quoting. */
    updateActions(reason) {
      const btn = this.root.querySelector('[data-add]');
      const note = this.root.querySelector('[data-reason]');
      if (!btn) return;
      const buying = !!this._resolved;
      btn.textContent = buying ? 'Add to Basket' : 'Add to Quote';
      btn.classList.toggle('cfg-btn--buy', buying);
      const text = buying ? '' : this.reasonText(reason);
      if (note) {
        note.textContent = text;
        note.hidden = !text;
      }
    }

    updatePrice() {
      const box = this.root.querySelector('[data-price]');
      if (!box) return;
      this._lastEstimate = null;

      const res = this.resolveVariant();
      this._resolved = res.variant;
      this.updateActions(res.reason);

      // A resolved variant prices from Shopify itself, never from the model, so
      // what the customer sees here and what checkout charges cannot diverge.
      if (res.variant) {
        const P = window.EEPricing;
        const f = (n) => '&pound;' + n.toFixed(2);
        const unit = res.variant.price / 100;
        const disc = P.batchDiscount(this.state.qty);
        const pct = Math.round(disc * 100);
        const next = P.nextTier(this.state.qty);
        let extra = '';
        if (pct) extra += `<span class="cfg-price-saving">&minus;${pct}% batch discount at checkout</span>`;
        if (next) {
          const gain = next.pct > 0 ? `${Math.round(next.pct * 100)}% off` : 'free set-up';
          extra += `${pct ? ' <span class="cfg-price-sep">&middot;</span> ' : ''}<span class="cfg-price-upsell">Add ${next.qty - this.state.qty} more for ${gain}</span>`;
        }
        box.innerHTML = `
          <div class="cfg-price-main"><b>${f(unit)}</b> / patch
            <span class="cfg-price-sep">&middot;</span> total <b>${f(unit * this.state.qty * (1 - disc))}</b></div>
          ${extra ? `<div class="cfg-price-batch">${extra}</div>` : ''}
          <div class="cfg-price-disc">Price as charged at checkout, including VAT &mdash; our
            <a href="/pages/pricing-guide">pricing guide</a> lists ex-VAT prices.
            Free set-up &amp; digitising included.</div>`;
        return;
      }

      const est = this.def.estimator ? this.def.estimator(this.state, this.state.qty) : null;
      if (!est) {
        box.innerHTML = this.def.estimator
          ? '<span class="cfg-price-note">Enter a size to see an estimated price.</span>' : '';
        return;
      }
      if (est.text) { box.innerHTML = `<span class="cfg-price-note">${esc(est.text)}</span>`; return; }
      const f = (n) => '&pound;' + n.toFixed(2);

      // Batch discount, and how many more units unlock the next tier.
      const pct = est.discount ? Math.round(est.discount * 100) : 0;
      const applied = pct
        ? `<span class="cfg-price-saving">&minus;${pct}% batch discount applied</span>`
        : '';
      let upsell = '';
      if (est.nextTier) {
        const more = est.nextTier.qty - this.state.qty;
        const gain = est.nextTier.pct > 0
          ? `${Math.round(est.nextTier.pct * 100)}% off`
          : 'free set-up &amp; digitising';
        upsell = `<span class="cfg-price-upsell">Add ${more} more for ${gain}</span>`;
      }

      box.innerHTML = `
        <div class="cfg-price-main">${est.from ? 'From ' : ''}<b>${f(est.unit)}</b> / patch
          <span class="cfg-price-sep">&middot;</span> est. total <b>${f(est.total)}</b>
          ${est.setup ? `<small>(incl. ${f(est.setup)} one-off setup)</small>` : ''}</div>
        ${applied || upsell ? `<div class="cfg-price-batch">${applied}${applied && upsell ? ' <span class="cfg-price-sep">&middot;</span> ' : ''}${upsell}</div>` : ''}
        <div class="cfg-price-disc">From our <a href="/pages/pricing-guide">pricing guide</a> excl. VAT.</div>`;
      this._lastEstimate = est;
    }

    renderField(f, n) {
      const full = ['upload', 'textarea', 'checks', 'shapes'].includes(f.type) || f.full ? ' cfg-field--full' : '';
      const label = `<span class="cfg-label">${n}. ${esc(f.label)}${f.hint ? ` <small>${esc(f.hint)}</small>` : ''}${f.help ? `<span class="cfg-help" title="${esc(f.help)}">?</span>` : ''}</span>`;
      let body = '';
      const v = this.val(f.id);

      switch (f.type) {
        case 'size': {
          const unit = this.state[f.id + '_unit'] || f.defaultUnit || 'mm';
          body = `<div class="cfg-size-row">
            <span class="cfg-size-input"><input class="cfg-input" data-k="w" type="number" min="1" inputmode="decimal" placeholder="Width" value="${esc(this.state[f.id + '_w'] || '')}"><span class="cfg-unit-tag">${esc(unit)}</span></span>
            <span class="cfg-size-x">&times;</span>
            <span class="cfg-size-input"><input class="cfg-input" data-k="h" type="number" min="1" inputmode="decimal" placeholder="Height" value="${esc(this.state[f.id + '_h'] || '')}"><span class="cfg-unit-tag">${esc(unit)}</span></span>
            <span class="cfg-unit-toggle">${['mm', 'cm', 'in'].map((u) => `<button type="button" data-unit="${u}" class="${u === unit ? 'is-active' : ''}">${u}</button>`).join('')}</span>
          </div>`;
          break;
        }
        case 'swatches': {
          const max = f.visible || 7;
          const expanded = !!this.state[f.id + '_expanded'];
          const shown = expanded ? f.options : f.options.slice(0, max);
          body = `<div class="cfg-swatches">
            ${shown.map((o) => `<button type="button" class="cfg-swatch ${v === o.name ? 'is-active' : ''}" data-swatch="${esc(o.name)}" title="${esc(o.name)}" style="background:${esc(o.hex)}"></button>`).join('')}
            ${f.options.length > max ? `<button type="button" class="cfg-swatch-more" data-more>${expanded ? 'LESS' : 'VIEW ALL'}</button>` : ''}
          </div><div class="cfg-swatch-name">${v ? esc(v) : ''}</div>`;
          break;
        }
        case 'select':
          body = `<select class="cfg-select" data-k="sel">${f.options.map((o) => `<option value="${esc(o)}" ${v === o ? 'selected' : ''}>${esc(o)}</option>`).join('')}</select>`;
          break;
        case 'pills':
          body = `<div class="cfg-pills">${f.options.map((o) => `<button type="button" class="cfg-pill ${v === o ? 'is-active' : ''}" data-pill="${esc(o)}">${esc(o)}</button>`).join('')}</div>`;
          break;
        case 'shapes':
          body = `<div class="cfg-shapes">${f.options.map((o) => `<button type="button" class="cfg-shape ${v === o ? 'is-active' : ''}" data-pill="${esc(o)}">${SHAPE_ICONS[o.toLowerCase()] || SHAPE_ICONS.custom}<span>${esc(o)}</span></button>`).join('')}</div>`;
          break;
        case 'checks':
          body = `<div class="cfg-checks">${f.options.map((o) => `
            <label class="cfg-check"><input type="checkbox" data-chk="${esc(o)}" ${v && v.includes(o) ? 'checked' : ''}><span class="cfg-box"></span>${esc(o)}</label>`).join('')}</div>`;
          break;
        case 'radio': {
          const enabled = this.isEnabled(f);
          body = `<div class="cfg-checks">${f.options.map((o) => `
            <label class="cfg-check ${!enabled && o !== f.disabledValue ? 'is-disabled' : ''}">
              <input type="radio" name="cfg-${this.def.id}-${f.id}" data-rad="${esc(o)}" ${v === o ? 'checked' : ''} ${!enabled && o !== f.disabledValue ? 'disabled' : ''}>
              <span class="cfg-box cfg-box--round"></span>${esc(o)}</label>`).join('')}
            ${!enabled && f.minQtyHint ? `<span class="cfg-check-hint">${esc(f.minQtyHint)}</span>` : ''}</div>`;
          break;
        }
        case 'upload':
          body = `<div class="cfg-upload" data-zone role="button" tabindex="0" aria-label="Upload artwork">
              <input type="file" accept="${esc(f.accept || 'image/png,image/jpeg')}" ${f.maxFiles !== 1 ? 'multiple' : ''}>
              ${ICON_UPLOAD}
              <div class="cfg-upload-text">Drag and drop your file here or <span>choose file</span></div>
              <div class="cfg-upload-hint">${esc(f.hint2 || 'PNG or JPEG — max file size 20MB')}</div>
            </div><ul class="cfg-files" data-files></ul>`;
          break;
        case 'textarea':
          body = `<textarea class="cfg-textarea" data-k="ta" maxlength="${f.maxLength || 250}" placeholder="${esc(f.placeholder || '')}">${esc(v || '')}</textarea>
            <div class="cfg-char-count"><span data-count>${(v || '').length}</span>/${f.maxLength || 250}</div>`;
          break;
        case 'text':
          body = `<input class="cfg-input" data-k="txt" type="text" maxlength="${f.maxLength || 120}" placeholder="${esc(f.placeholder || '')}" value="${esc(v || '')}">`;
          break;
      }
      return `<div class="cfg-field${full}" data-field="${esc(f.id)}">${label}${body}<div class="cfg-field-error" data-err></div></div>`;
    }

    bindFields() {
      (this.def.fields || []).filter((f) => this.isVisible(f)).forEach((f) => {
        const wrap = this.fieldsEl.querySelector(`[data-field="${f.id}"]`);
        if (!wrap) return;

        if (f.type === 'size') {
          wrap.querySelector('[data-k="w"]').addEventListener('input', (e) => { this.state[f.id + '_w'] = e.target.value; });
          wrap.querySelector('[data-k="h"]').addEventListener('input', (e) => { this.state[f.id + '_h'] = e.target.value; });
          wrap.querySelectorAll('[data-unit]').forEach((b) => b.addEventListener('click', () => {
            this.state[f.id + '_unit'] = b.dataset.unit; this.renderFields();
          }));
        }
        if (f.type === 'swatches') {
          wrap.querySelectorAll('[data-swatch]').forEach((b) => b.addEventListener('click', () => {
            this.state[f.id] = b.dataset.swatch; this.renderFields();
          }));
          const more = wrap.querySelector('[data-more]');
          if (more) more.addEventListener('click', () => {
            this.state[f.id + '_expanded'] = !this.state[f.id + '_expanded']; this.renderFields();
          });
        }
        if (f.type === 'select') wrap.querySelector('[data-k="sel"]').addEventListener('change', (e) => { this.state[f.id] = e.target.value; });
        if (f.type === 'pills' || f.type === 'shapes') {
          wrap.querySelectorAll('[data-pill]').forEach((b) => b.addEventListener('click', () => {
            this.state[f.id] = b.dataset.pill; this.renderFields();
          }));
        }
        if (f.type === 'checks') {
          wrap.querySelectorAll('[data-chk]').forEach((c) => c.addEventListener('change', () => {
            const set = new Set(this.state[f.id] || []);
            c.checked ? set.add(c.dataset.chk) : set.delete(c.dataset.chk);
            this.state[f.id] = [...set];
          }));
        }
        if (f.type === 'radio') {
          wrap.querySelectorAll('[data-rad]').forEach((r) => r.addEventListener('change', () => { this.state[f.id] = r.dataset.rad; }));
        }
        if (f.type === 'textarea') {
          const ta = wrap.querySelector('[data-k="ta"]');
          ta.addEventListener('input', () => {
            this.state[f.id] = ta.value;
            wrap.querySelector('[data-count]').textContent = ta.value.length;
          });
        }
        if (f.type === 'text') {
          wrap.querySelector('[data-k="txt"]').addEventListener('input', (e) => { this.state[f.id] = e.target.value; });
        }
        if (f.type === 'upload') this.bindUpload(f, wrap);
      });
    }

    bindUpload(f, wrap) {
      const zone = wrap.querySelector('[data-zone]');
      const input = zone.querySelector('input[type="file"]');
      const list = wrap.querySelector('[data-files]');
      const maxMB = f.maxSizeMB || 20;
      const maxFiles = f.maxFiles || 3;
      const err = wrap.querySelector('[data-err]');

      const renderList = () => {
        list.innerHTML = this.state.files.map((file, i) =>
          `<li><span>${esc(file.name)} (${(file.size / 1048576).toFixed(1)}MB)</span><button type="button" data-rm="${i}" aria-label="Remove file">&times;</button></li>`).join('');
        list.querySelectorAll('[data-rm]').forEach((b) => b.addEventListener('click', () => {
          this.state.files.splice(+b.dataset.rm, 1); renderList();
        }));
      };
      this._renderFileList = renderList;
      renderList();

      const accept = (files) => {
        err.textContent = '';
        for (const file of files) {
          if (!/\.(png|jpe?g)$/i.test(file.name)) { err.textContent = 'PNG or JPEG files only.'; continue; }
          if (file.size > maxMB * 1048576) { err.textContent = `Files must be under ${maxMB}MB.`; continue; }
          if (this.state.files.length >= maxFiles) { err.textContent = `Maximum ${maxFiles} files.`; break; }
          this.state.files.push(file);
        }
        renderList();
      };
      zone.addEventListener('click', () => input.click());
      zone.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); input.click(); } });
      input.addEventListener('change', () => { accept([...input.files]); input.value = ''; });
      ['dragover', 'dragenter'].forEach((ev) => zone.addEventListener(ev, (e) => { e.preventDefault(); zone.classList.add('is-drag'); }));
      ['dragleave', 'drop'].forEach((ev) => zone.addEventListener(ev, (e) => { e.preventDefault(); zone.classList.remove('is-drag'); }));
      zone.addEventListener('drop', (e) => accept([...e.dataTransfer.files]));
    }

    validate() {
      let ok = true;
      let firstWrap = null;
      (this.def.fields || []).filter((f) => this.isVisible(f) && f.required).forEach((f) => {
        const wrap = this.fieldsEl.querySelector(`[data-field="${f.id}"]`);
        const err = wrap && wrap.querySelector('[data-err]');
        let missing = false;
        if (f.type === 'size') missing = !(parseFloat(this.state[f.id + '_w']) > 0 && parseFloat(this.state[f.id + '_h']) > 0);
        else if (f.type === 'checks') missing = false;
        else if (f.type === 'upload') missing = this.state.files.length === 0;
        else missing = !this.state[f.id];
        if (missing) {
          ok = false;
          if (!firstWrap) firstWrap = wrap;
          if (err) err.textContent = f.requiredMsg || 'This field is required.';
        } else if (err) err.textContent = '';
      });
      if (firstWrap) firstWrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return ok;
    }

    summary() {
      const parts = [];
      (this.def.fields || []).filter((f) => this.isVisible(f)).forEach((f) => {
        let v;
        if (f.type === 'size') {
          const w = this.state[f.id + '_w'], h = this.state[f.id + '_h'];
          if (w || h) v = `${w || '?'} × ${h || '?'} ${this.state[f.id + '_unit'] || f.defaultUnit || 'mm'}`;
        } else if (f.type === 'checks') v = (this.state[f.id] || []).join(', ') || null;
        else if (f.type === 'upload') v = this.state.files.length ? this.state.files.map((x) => x.name).join(', ') : null;
        else v = this.state[f.id];
        if (v) parts.push({ label: f.label, value: String(v) });
      });
      return parts;
    }

    /* Posts to Shopify's cart. Mirrors assets/product-form.js so Dawn's cart
       notification updates: multipart body (never set Content-Type - the browser
       must supply the multipart boundary), and the sections the notification
       needs to re-render. */
    async addToCart() {
      if (!this.validate()) return;
      const btn = this.root.querySelector('[data-add]');
      const note = this.root.querySelector('[data-reason]');
      btn.disabled = true;

      try {
        const fd = new FormData();
        fd.append('id', this._resolved.id);
        fd.append('quantity', this.state.qty);

        const props = this.def.buyable.lineItemProps
          ? this.def.buyable.lineItemProps(this.state) : {};
        Object.keys(props).forEach((k) => { if (props[k]) fd.append(`properties[${k}]`, props[k]); });
        // Numbered keys because one property name holds one file.
        (this.state.files || []).forEach((file, i) => {
          fd.append(`properties[Artwork ${i + 1}]`, file, file.name);
        });
        // Underscore-prefixed properties are hidden from the customer in cart
        // but still land on the order - invaluable in production.
        fd.append('properties[_config]', JSON.stringify({ ...this.state, files: undefined }));

        const cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartEl && cartEl.getSectionsToRender) {
          fd.append('sections', cartEl.getSectionsToRender().map((s) => s.id));
          fd.append('sections_url', window.location.pathname);
          if (cartEl.setActiveElement) cartEl.setActiveElement(document.activeElement);
        }

        const routes = window.routes || {};
        const res = await fetch(routes.cart_add_url || '/cart/add', {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: fd,
        });
        const json = await res.json();

        if (json.status) {
          if (note) { note.textContent = json.description || json.message || 'Could not add to basket.'; note.hidden = false; }
          return;
        }

        if (cartEl && cartEl.renderContents) cartEl.renderContents(json);
        else window.location = routes.cart_url || '/cart';

        const msg = this.root.querySelector('[data-added]');
        if (msg) {
          msg.textContent = 'Added to your basket ✓';
          msg.classList.add('is-visible');
          setTimeout(() => msg.classList.remove('is-visible'), 2400);
        }
      } catch (err) {
        if (note) { note.textContent = 'Could not reach the basket. Please try again.'; note.hidden = false; }
      } finally {
        btn.disabled = false;
      }
    }

    addToQuote() {
      if (!this.validate()) return;
      this.basket.add({
        productId: this.def.id,
        productTitle: this.def.title,
        qty: this.state.qty,
        summary: this.summary(),
        state: { ...this.state, files: undefined },
        files: [...this.state.files],
        estimate: this._lastEstimate && this._lastEstimate.unit ? { ...this._lastEstimate } : null,
      });
      const msg = this.root.querySelector('[data-added]');
      msg.classList.add('is-visible');
      setTimeout(() => msg.classList.remove('is-visible'), 2400);
    }
  }

  /* ══════════════════ Quote basket ══════════════════ */

  class Basket {
    constructor(cfg) {
      this.cfg = cfg;
      this.items = [];
      this._turnstileToken = null;
      this._turnstileReason = null;   // why no token, for the server to record
      this._buildUI();
    }

    add(item) { this.items.push(item); this._refresh(); }
    remove(i) { this.items.splice(i, 1); this._refresh(); if (!this.items.length) this._close(); }

    _buildUI() {
      const bar = document.createElement('div');
      bar.className = 'cfg-basket-bar cfg-wrapper';
      bar.innerHTML = `<span class="cfg-basket-count"><b data-count>0</b> item(s) in your quote</span>
        <button type="button" class="cfg-btn" data-open>Review &amp; Send</button>`;
      document.body.appendChild(bar);
      this.bar = bar;

      const overlay = document.createElement('div');
      overlay.className = 'cfg-overlay cfg-wrapper';
      overlay.innerHTML = `
        <div class="cfg-panel" role="dialog" aria-modal="true" aria-label="Your quote">
          <h3>Your Quote Request</h3>
          <div class="cfg-panel-items" data-items></div>
          <div class="cfg-contact-grid">
            <div class="cfg-field"><span class="cfg-label">First name</span><input class="cfg-input" data-c="firstName" autocomplete="given-name"></div>
            <div class="cfg-field"><span class="cfg-label">Last name</span><input class="cfg-input" data-c="lastName" autocomplete="family-name"></div>
            <div class="cfg-field"><span class="cfg-label">Email</span><input class="cfg-input" data-c="email" type="email" autocomplete="email"></div>
            <div class="cfg-field"><span class="cfg-label">Phone <small>(optional)</small></span><input class="cfg-input" data-c="phone" type="tel" autocomplete="tel"></div>
            <div class="cfg-field cfg-field--full"><span class="cfg-label">Need by date <small>(optional)</small></span><input class="cfg-input" data-c="deadline" type="date"></div>
          </div>
          <div class="cfg-hp" aria-hidden="true"><input data-hp name="company_website" type="text" tabindex="-1" autocomplete="off"></div>
          <div class="cfg-captcha" data-captcha></div>
          <div class="cfg-panel-foot">
            <button type="button" class="cfg-btn cfg-btn--ghost" data-close>Keep configuring</button>
            <span class="cfg-panel-status" data-status></span>
            <button type="button" class="cfg-btn" data-send>Send Quote Request</button>
          </div>
        </div>`;
      document.body.appendChild(overlay);
      this.overlay = overlay;

      bar.querySelector('[data-open]').addEventListener('click', () => this._open());
      overlay.querySelector('[data-close]').addEventListener('click', () => this._close());
      overlay.addEventListener('click', (e) => { if (e.target === overlay) this._close(); });
      overlay.querySelector('[data-send]').addEventListener('click', () => this._send());
    }

    _refresh() {
      this.bar.querySelector('[data-count]').textContent = this.items.length;
      this.bar.classList.toggle('is-visible', this.items.length > 0);
      const box = this.overlay.querySelector('[data-items]');
      box.innerHTML = this.items.map((it, i) => `
        <div class="cfg-panel-item">
          <div>
            <p class="cfg-panel-item-title">${esc(it.productTitle)} — ${it.qty} units${it.estimate ? ` <span class="cfg-panel-item-est">${it.estimate.from ? 'from ' : ''}~&pound;${it.estimate.total.toFixed(2)}</span>` : ''}</p>
            <p class="cfg-panel-item-spec">${it.summary.map((s) => `${esc(s.label)}: ${esc(s.value)}`).join(' &middot; ')}</p>
          </div>
          <button type="button" data-rm="${i}" aria-label="Remove item">&times;</button>
        </div>`).join('');
      box.querySelectorAll('[data-rm]').forEach((b) => b.addEventListener('click', () => this.remove(+b.dataset.rm)));
    }

    _open() {
      this._refresh();
      this.overlay.classList.add('is-open');
      const wrap = this.overlay.querySelector('[data-captcha]');
      if (!this.cfg.captchaSiteKey || wrap.dataset.rendered) return;

      // turnstile/v0/api.js is async, so global.turnstile is often still
      // undefined when the basket is first opened. Testing it once and moving
      // on left no widget on screen while _submit() still demanded a token —
      // an unexplained dead end. Wait for it, and treat "never arrived" the
      // same as an error-callback so submission degrades open rather than
      // blocking a real customer.
      const STEP = 150;
      const SCRIPT_LIMIT = 20000;   // the script genuinely failing to load
      const LAYOUT_LIMIT = 90000;   // the panel simply not laid out yet
      let waited = 0;

      // The widget must be rendered into a box that actually has layout.
      // classList.add('is-open') above does not lay the overlay out until the
      // next frame, so render() used to run against a display:none, 0x0
      // container: Turnstile created the widget and returned an id, but the
      // challenge iframe had nowhere to go. When Turnstile chose a silent pass
      // a token still arrived and everything looked fine; when it decided an
      // interactive challenge was needed — far likelier from an office IP —
      // nothing was drawn, no token ever came, and _send() blocked forever on
      // "Please complete the security check" with no check on screen.
      const isLaidOut = () => {
        // Width and actually-being-rendered is all Turnstile needs; the height
        // comes from the widget itself, so requiring height on a container we
        // just emptied is circular. offsetParent === null covers display:none
        // and any hidden ancestor.
        if (!wrap.isConnected || wrap.offsetParent === null) return false;
        return wrap.getBoundingClientRect().width > 0;
      };

      const mount = () => {
        const ready = global.turnstile && typeof global.turnstile.render === 'function';
        if (ready && isLaidOut()) {
          wrap.dataset.rendered = '1';
          try {
            global.turnstile.render(wrap, {
              sitekey: this.cfg.captchaSiteKey,
              theme: 'dark',
              action: 'quote-basket',   // separates this tool from the form in Turnstile analytics
              callback: (t) => { this._turnstileToken = t; this._turnstileReason = null; },
              'expired-callback': () => { this._turnstileToken = null; this._turnstileReason = 'expired'; },
              // If the widget can't load (e.g. site key not registered for this
              // domain), don't hard-block quote submissions — degrade gracefully.
              'error-callback': (code) => { this._turnstileToken = null; this._turnstileReason = 'error-' + code; this._turnstileFailed = true; wrap.style.display = 'none'; },
            });
          } catch (e) {
            this._turnstileReason = 'render-threw';
            this._turnstileFailed = true;
            wrap.style.display = 'none';
          }
          return;
        }
        waited += STEP;
        // Script never arrived: degrade open rather than block a real customer.
        if (!ready && waited >= SCRIPT_LIMIT) {
          this._turnstileReason = 'script-timeout';
          this._turnstileFailed = true;
          wrap.style.display = 'none';
          return;
        }
        // Script is fine, the panel just is not laid out yet — keep watching
        // rather than treating "not visible" as a failure.
        if (waited >= LAYOUT_LIMIT) {
          // Never silent: this exact exit hid a CSS bug for days.
          this._turnstileReason = 'layout-timeout';
          console.warn('[EECFG] captcha container never laid out - check for CSS hiding the empty slot');
          this._turnstileFailed = true;
          wrap.style.display = 'none';
          return;
        }
        setTimeout(mount, STEP);
      };
      mount();
    }

    _close() { this.overlay.classList.remove('is-open'); }

    _status(msg, cls) {
      const el = this.overlay.querySelector('[data-status]');
      el.textContent = msg;
      el.className = 'cfg-panel-status' + (cls ? ' ' + cls : '');
    }

    async _send() {
      const contact = {};
      this.overlay.querySelectorAll('[data-c]').forEach((i) => { contact[i.dataset.c] = i.value.trim(); });
      if (!contact.firstName || !contact.lastName || !contact.email) {
        this._status('Please fill in your name and email.', 'is-error'); return;
      }
      if (!/.+@.+\..+/.test(contact.email)) { this._status('Please enter a valid email address.', 'is-error'); return; }
      if (!this.items.length) { this._status('Your quote is empty.', 'is-error'); return; }
      // Best-effort bot check — never a gate. See the note in quote.js _submit():
      // every token-gated version of this stranded real customers. The server
      // verifies the token when present and flags the quote when it is not.
      const btn = this.overlay.querySelector('[data-send]');
      btn.disabled = true;
      this._status('Sending…');
      try {
        const hpEl = this.overlay.querySelector('[data-hp]');
        await this.cfg.onSubmit({
          contact,
          items: this.items,
          turnstileToken: this._turnstileToken,
          turnstileClientReason: this._turnstileToken ? null : (this._turnstileReason || 'no-token'),
          hp: hpEl ? hpEl.value : '',
        });
        this._status('Sent! We’ll be in touch with your quote shortly.', 'is-success');
        this.items = [];
        this._refresh();
        btn.disabled = false;
        setTimeout(() => this._close(), 2600);
      } catch (err) {
        console.error('[EECFG] submit error:', err);
        this._status('Something went wrong — please try again.', 'is-error');
        btn.disabled = false;
      }
    }
  }

  /* ══════════════════ Public API ══════════════════ */

  const EEConfigurator = {
    _basket: null,
    init(rootId, productIds, cfg, variantMap) {
      const root = document.getElementById(rootId);
      if (!root || !cfg) return;
      if (!this._basket) this._basket = new Basket(cfg);
      root.classList.add('cfg-wrapper');
      productIds.forEach((pid) => {
        const def = cfg.products[pid];
        const mount = document.createElement('div');
        root.appendChild(mount);
        if (!def) { mount.innerHTML = `<div class="cfg-card cfg-construction"><h3>Currently unavailable</h3><p>Please <a href="/pages/contact">contact us</a> and we will quote this for you.</p></div>`; return; }
        if (def.underConstruction) {
          mount.innerHTML = `<div class="cfg-card cfg-construction"><h3>${esc(def.title)}</h3><p>${esc(def.caption || 'This area is under construction — please contact us for a quote.')}</p><a class="cfg-btn" href="${esc(def.ctaLink || '/pages/contact')}" style="text-decoration:none">Contact us for a quote</a></div>`;
          return;
        }
        new ProductCard(mount, def, this._basket, variantMap);
      });
    },
  };

  global.EEConfigurator = EEConfigurator;
})(typeof window !== 'undefined' ? window : this);
